import { normalize } from 'normalizr';

import { selectAccountId } from 'shared/store/modules/accounts/selectors';
import { addEntities, deleteEntities } from 'shared/store/modules/entities';
import {
  createProjectRequest,
  createProjectResponse,
  customerProjectExportRequest,
  customerProjectExportResponse,
  customerProjectFetchRequest,
  customerProjectFetchResponse,
  customerProjectsFetchRequest,
  customerProjectsFetchResponse,
  fetchAllProjectsRequest,
  fetchAllProjectsResponse,
  fetchAutoAllocatableProjectsRequest,
  fetchAutoAllocatableProjectsResponse,
  fetchProjectAllocationsRequest,
  fetchProjectAllocationsResponse,
  fetchProjectDetailsRequest,
  fetchProjectDetailsResponse,
  fetchProjectRequest,
  fetchProjectResponse,
  exportProjectPaymentsRequest,
  exportProjectPaymentsResponse,
  exportProjectProductionRequest,
  exportProjectProductionResponse,
  updateProjectRequest,
  updateProjectResponse,
  uploadProjectImageRequest,
  uploadProjectImageResponse
} from 'shared/store/modules/projects/actions';
import toast from 'shared/utilities/toast';

export const customerProjectsFetch = () => async (dispatch, getState, { api, schemas }) => {
  dispatch(customerProjectsFetchRequest());

  const customerId = selectAccountId(getState());

  try {
    const { data } = await api.subscriber.getCustomerProjects(customerId);
    const entities = normalize(data, schemas.projects);
    dispatch(addEntities(entities));
    return dispatch(customerProjectsFetchResponse(entities));
  } catch (err) {
    return dispatch(customerProjectsFetchResponse(err));
  }
};

export const customerProjectFetch = projectId => async (dispatch, getState, { api }) => {
  dispatch(customerProjectFetchRequest());

  const customerId = selectAccountId(getState());

  try {
    const { data: project } = await api.subscriber.getCustomerProject(customerId, projectId);
    return dispatch(customerProjectFetchResponse(project));
  } catch (err) {
    return dispatch(customerProjectFetchResponse(err));
  }
};

export const customerProjectExport = projectId => async (dispatch, getState, { api }) => {
  dispatch(customerProjectExportRequest());

  const customerId = selectAccountId(getState());

  try {
    const { data: project } = await api.subscriber.exportCustomerProject(customerId, projectId);
    return dispatch(customerProjectExportResponse(project));
  } catch (err) {
    return dispatch(customerProjectExportResponse(err));
  }
};

export const createProject = project => async (dispatch, _getState, { api, schemas }) => {
  dispatch(createProjectRequest());

  try {
    let { data } = await api.project.create(project);
    data = normalize(data, schemas.project);

    toast.success('New project created');
    dispatch(addEntities(data));
    return dispatch(createProjectResponse(data));
  } catch (err) {
    toast.error(err);
    return dispatch(createProjectResponse(err));
  }
};

export const fetchProject = id => async (dispatch, _getState, { api, schemas }) => {
  dispatch(fetchProjectRequest());

  try {
    const { data } = await api.project.fetchOne(id);
    const project = normalize(data, schemas.project);

    dispatch(addEntities(project));
    return dispatch(fetchProjectResponse(project));
  } catch (err) {
    return dispatch(fetchProjectResponse(err));
  }
};

export const fetchProjectDetails = id => async (dispatch, _getState, { api, schemas }) => {
  dispatch(fetchProjectDetailsRequest());

  try {
    let { data } = await api.project.fetchDetails(id);
    data = normalize(data, schemas.project);

    dispatch(addEntities(data));
    return dispatch(fetchProjectDetailsResponse(data));
  } catch (err) {
    return dispatch(fetchProjectDetailsResponse(err));
  }
};

export const fetchProjectAllocations = id => async (dispatch, _getState, { api, schemas }) => {
  dispatch(fetchProjectAllocationsRequest());

  try {
    let { data: project } = await api.project.fetchAllocations(id);

    const { utilityId } = project;

    let { data: subscribers } = await api.subscriber.fetchAllByUtility(utilityId);

    subscribers = subscribers.map(subscriber => ({
      ...subscriber,
      utilityId,
      premises: subscriber.premises.map(premise => ({
        ...premise,
        allocations: []
      }))
    }));

    project.allocations = project.allocations.map(allocation => ({
      ...allocation,
      project: {
        id
      }
    }));

    // The order of these matter :|
    // ----------------------------------------------------------------------------
    subscribers = normalize(subscribers, schemas.subscribers);
    dispatch(addEntities(subscribers));
    project = normalize(project, schemas.project);
    dispatch(addEntities(project));
    // ----------------------------------------------------------------------------
  } catch (err) {
    return dispatch(fetchProjectAllocationsResponse(err));
  }
};

export const fetchProductionExport = (id, period, type) => async (dispatch, _getState, { api, schemas }) => {
  dispatch(exportProjectProductionRequest());

  try {
    let data;

    if (period === 'Lifetime') {
      data = await api.project.exportProductionLifetime(id, type);
      return dispatch(exportProjectProductionResponse(data));
    } else {
      const year = period.split('-')[0];
      const month = period.split('-')[1];
      data = await api.project.exportProductionByMonth(id, year, month, type);
      return dispatch(exportProjectProductionResponse(data));
    }
  } catch (err) {
    return dispatch(exportProjectProductionResponse(err));
  }
};

export const fetchPaymentsExport = (id, period, type) => async (dispatch, _getState, { api, schemas }) => {
  dispatch(exportProjectPaymentsRequest());

  try {
    let data;

    if (period === 'Lifetime') {
      data = await api.project.exportPaymentsLifetime(id, type);
      return dispatch(exportProjectPaymentsResponse(data));
    } else {
      var year = period.split('-')[0];
      var month = period.split('-')[1];
      data = await api.project.exportPaymentsByMonth(id, year, month, type);
      return dispatch(exportProjectPaymentsResponse(data));
    }
  } catch (err) {
    return dispatch(exportProjectPaymentsResponse(err));
  }
};

export const fetchProjects = params => async (dispatch, _getState, { api, schemas }) => {
  dispatch(fetchAllProjectsRequest());

  try {
    const { data } = await api.project.fetchAll(params);
    const projects = normalize(data, schemas.projects);
    dispatch(addEntities(projects));
    return dispatch(fetchAllProjectsResponse(projects));
  } catch (err) {
    dispatch(fetchAllProjectsResponse(err));
  }
};

export const fetchAutoAllocatableProjects = utilityId => async (dispatch, _getState, { api }) => {
  dispatch(fetchAutoAllocatableProjectsRequest());

  try {
    const { data } = await api.project.fetchAutoAllocatableCustomers(utilityId);

    return dispatch(fetchAutoAllocatableProjectsResponse(data));
  } catch (err) {
    return dispatch(fetchAutoAllocatableProjectsResponse(err));
  }
};

export const updateProject = (id, updates) => async (dispatch, _getState, { api, schemas }) => {
  dispatch(updateProjectRequest());

  try {
    await api.project.update(id, updates);
    const data = normalize({ id, ...updates }, schemas.project);

    toast.success('Project updated');
    dispatch(addEntities(data));
    return dispatch(updateProjectResponse(data));
  } catch (err) {
    toast.error(err);
    return dispatch(updateProjectResponse(err));
  }
};

export const uploadImage = formData => async (dispatch, _getState, { api }) => {
  dispatch(uploadProjectImageRequest());

  try {
    const { data } = await api.project.uploadImage(formData);

    if (data.success) {
      // Force update component with same photo URL
      const timestamp = new Date().getTime();

      dispatch(
        uploadProjectImageResponse({
          id: formData.get('projectId'),
          photo: `${data.success}?timestamp=${timestamp}`
        })
      );

      toast.success('Photo uploaded');
    }

    return dispatch(uploadProjectImageResponse(data));
  } catch (err) {
    return dispatch(uploadProjectImageResponse(err));
  }
};

export const deleteProject = id => async (dispatch, _getState, { api, schemas }) => {
  dispatch(updateProjectRequest());

  try {
    await api.project.delete(id);
    toast.success('Project deleted');
    return dispatch(deleteEntities({ id }));
  } catch (err) {
    toast.error(err);
    return dispatch(updateProjectResponse(err));
  }
};
